<template>
  <div id="healthManage">
    <page-header title="文章管理" :count="total" unit="篇">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openEditor(null)">新增文章</el-button>
      </template>
      <el-select v-model="searchForm.status" clearable placeholder="状态" size="small">
        <el-option label="已发布" :value="1" />
        <el-option label="草稿" :value="0" />
      </el-select>
      <el-input v-model="searchForm.keyword" class="ph-search" placeholder="搜索文章标题..."
        prefix-icon="el-icon-search" clearable size="small" @keyup.enter.native="fetchList()" />
      <el-button type="primary" size="small" icon="el-icon-search" @click="fetchList()">搜索</el-button>
      <el-button size="small" @click="searchForm = {}; fetchList()">重置</el-button>
    </page-header>

    <!-- 文章列表 -->
    <div class="article-table" v-loading="loading">
      <el-table :data="articles" stripe size="small" class="app-table" style="width: 100%">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="title" label="文章标题" min-width="200" show-overflow-tooltip />
        <el-table-column prop="category" label="分类" width="100" />
        <el-table-column prop="author" label="作者" width="100" />
        <el-table-column prop="viewCount" label="阅读量" width="80" align="center">
          <template slot-scope="{ row }">
            <span class="view-count">{{ row.viewCount || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini" effect="dark">
              {{ row.status === 1 ? '已发布' : '草稿' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160">
          <template slot-scope="{ row }">
            {{ $moment(row.createTime).format('YYYY-MM-DD HH:mm') }}
          </template>
        </el-table-column>
        <!-- 操作列：主按钮 + 下拉更多 -->
        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template slot-scope="{ row }">
            <div class="action-group">
              <el-tooltip content="编辑" placement="top" :open-delay="300">
                <el-button size="mini" type="primary" icon="el-icon-edit" circle
                           @click="openEditor(row)" />
              </el-tooltip>
              <el-tooltip :content="row.status === 1 ? '下架' : '发布'" placement="top" :open-delay="300">
                <el-button size="mini" :type="row.status === 1 ? 'warning' : 'success'"
                           :icon="row.status === 1 ? 'el-icon-download' : 'el-icon-upload2'" circle
                           @click="togglePublish(row)" />
              </el-tooltip>
              <el-tooltip content="删除" placement="top" :open-delay="300">
                <el-button size="mini" type="danger" icon="el-icon-delete" circle
                           @click="deleteArticle(row)" />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="app-pagination" v-if="total > pageSize">
        <el-pagination background layout="prev, pager, next, total"
          :total="total" :page-size="pageSize"
          :current-page.sync="pageNum" @current-change="fetchList()" />
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog :title="editForm.id ? '编辑文章' : '新增文章'" :visible.sync="dialogVisible"
               width="900px" custom-class="article-dialog" :close-on-click-modal="false"
               top="3vh" :destroy-on-close="true">
      <el-form :model="editForm" label-width="80px" ref="editFormRef">
        <!-- 第一行：标题 -->
        <el-form-item label="标题" prop="title" :rules="[{ required: true, message: '请输入标题' }]">
          <el-input v-model="editForm.title" placeholder="请输入文章标题" />
        </el-form-item>
        <!-- 第二行：分类 + 作者（并排） -->
        <div class="form-row">
          <el-form-item label="分类" class="form-row-item">
            <el-select v-model="editForm.category" placeholder="选择分类">
              <el-option label="健康科普" value="健康科普" />
              <el-option label="医学知识" value="医学知识" />
              <el-option label="用药指导" value="用药指导" />
              <el-option label="疾病预防" value="疾病预防" />
            </el-select>
          </el-form-item>
          <el-form-item label="作者" class="form-row-item">
            <el-input v-model="editForm.author" placeholder="请输入作者名" />
          </el-form-item>
        </div>
        <!-- 封面图 -->
        <el-form-item label="封面图">
          <el-input v-model="editForm.coverUrl" placeholder="封面图URL（可选）" />
        </el-form-item>
        <!-- 富文本内容 -->
        <el-form-item label="内容" prop="content" :rules="[{ required: true, message: '请输入内容' }]">
          <div class="editor-wrapper">
            <quill-editor ref="articleEditor" v-model="editForm.content"
                          :options="editorOption" class="article-editor" />
          </div>
        </el-form-item>
        <!-- 状态 -->
        <el-form-item label="状态">
          <el-radio-group v-model="editForm.status">
            <el-radio :label="1">发布</el-radio>
            <el-radio :label="0">草稿</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveArticle">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import service from '@/request/request.js'

// 引入 vue-quill-editor 富文本组件
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import PageHeader from '@/components/PageHeader.vue'

// 工具栏配置（精简版，适合文章编辑）
const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],           // 标题
  ['bold', 'italic', 'underline', 'strike'],           // 加粗、斜体、下划线、删除线
  [{ color: [] }, { background: [] }],                 // 字体颜色、背景色
  [{ list: 'ordered' }, { list: 'bullet' }],           // 有序 / 无序列表
  [{ indent: '-1' }, { indent: '+1' }],                // 缩进
  [{ align: [] }],                                     // 对齐
  ['blockquote', 'code-block'],                        // 引用、代码块
  ['link', 'image'],                                   // 链接、图片
  ['clean']                                            // 清除格式
]

export default {
  name: 'HealthManage',
  components: { quillEditor, PageHeader },
  data() {
    return {
      loading: false,
      saving: false,
      dialogVisible: false,
      articles: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      searchForm: {},
      editForm: {
        id: null,
        title: '',
        content: '',
        category: '健康科普',
        author: '',
        coverUrl: '',
        status: 1
      },
      // 富文本编辑器配置
      editorOption: {
        placeholder: '请输入文章内容...',
        theme: 'snow',
        modules: {
          toolbar: {
            container: toolbarOptions
          }
        }
      }
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    /** 获取文章列表 */
    fetchList() {
      this.loading = true
      service({
        url: '/api/health/article/manage/list',
        method: 'get',
        params: {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          keyword: this.searchForm.keyword || undefined,
          status: this.searchForm.status !== undefined ? this.searchForm.status : undefined
        }
      }).then(res => {
        if (res.code === 200) {
          this.articles = res.data.records || []
          this.total = res.data.total || 0
        }
      }).finally(() => {
        this.loading = false
      })
    },

    /** 打开编辑弹窗 */
    openEditor(row) {
      if (row) {
        // 编辑模式：只提取可编辑字段，避免把 createTime/updateTime 等时间字段带入请求导致反序列化失败
        this.editForm = {
          id: row.id,
          title: row.title,
          content: row.content,
          category: row.category,
          author: row.author,
          coverUrl: row.coverUrl,
          status: row.status
        }
      } else {
        // 新增模式
        this.editForm = {
          id: null,
          title: '',
          content: '',
          category: '健康科普',
          author: '',
          coverUrl: '',
          status: 1
        }
      }
      this.dialogVisible = true
    },

    /** 保存文章 */
    saveArticle() {
      this.$refs.editFormRef.validate(valid => {
        if (!valid) return
        this.saving = true
        service({
          url: '/api/health/article/manage/save',
          method: 'post',
          data: this.editForm
        }).then(res => {
          if (res.code === 200) {
            this.$message.success(res.data || '保存成功')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(res.msg || '保存失败')
          }
        }).finally(() => {
          this.saving = false
        })
      })
    },

    /** 发布/下架 */
    togglePublish(row) {
      const newStatus = row.status === 1 ? 0 : 1
      const label = newStatus === 1 ? '发布' : '下架'
      this.$confirm(`确认${label}文章「${row.title}」？`, '提示', { type: 'warning' })
        .then(() => {
          service({
            url: `/api/health/article/manage/publish/${row.id}`,
            method: 'put',
            params: { status: newStatus }
          }).then(res => {
            if (res.code === 200) {
              this.$message.success(`${label}成功`)
              this.fetchList()
            }
          })
        })
        .catch(() => {})
    },

    /** 删除文章 */
    deleteArticle(row) {
      this.$confirm(`确认删除文章「${row.title}」？删除后不可恢复。`, '删除确认', { type: 'warning' })
        .then(() => {
          service({
            url: `/api/health/article/manage/${row.id}`,
            method: 'delete'
          }).then(res => {
            if (res.code === 200) {
              this.$message.success('删除成功')
              this.fetchList()
            }
          })
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
#healthManage {
  padding: 20px;
  min-height: 100vh;
  background: #f5f7fa;
}

/* .page-header / .filter-section 已统一到 PageHeader 组件 */

/* ===== 表格 ===== */
.article-table {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);

  .view-count {
    font-weight: 600;
    color: #409eff;
  }
}

/* ===== 操作按钮组 ===== */
.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pagination {
  display: flex;
  justify-content: center;
  padding: 20px 0 0;
}

/* ===== 表单并排布局 ===== */
.form-row {
  display: flex;
  gap: 20px;

  .form-row-item {
    flex: 1;
  }
}

/* ===== 富文本编辑器容器 ===== */
.editor-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}
</style>

<!-- 富文本编辑器全局样式（不能 scoped，否则 Quill 工具栏样式会失效） -->
<style lang="scss">
/* 文章编辑器 — 编辑区高度 */
.article-dialog {
  .article-editor {
    .ql-editor {
      min-height: 320px;
      max-height: 50vh;
      overflow-y: auto;
      font-size: 14px;
      line-height: 1.8;
    }

    .ql-toolbar.ql-snow {
      border: none;
      border-bottom: 1px solid #e4e7ed;
      background: #fafafa;
      border-radius: 4px 4px 0 0;
      padding: 8px 12px;
    }

    .ql-container.ql-snow {
      border: none;
    }
  }

  /* 弹窗内部间距优化 */
  .el-dialog__body {
    padding: 16px 24px;
    max-height: 80vh;
    overflow-y: auto;
  }
}
</style>
